
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "@/components/SearchBar";
import EventFilters from "@/components/EventFilters";
import EventCard from "@/components/EventCard";
import FeaturedEvent from "@/components/FeaturedEvent";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import type { User } from "@supabase/supabase-js";

const Discover = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [featuredEvent, setFeaturedEvent] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check current auth status
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [searchQuery, dateRange, user]);

  const fetchEvents = async () => {
    try {
      let query = supabase
        .from('events')
        .select(`
          *,
          profiles:user_id (
            email,
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false });

      if (searchQuery) {
        query = query.ilike('title', `%${searchQuery}%`);
      }

      if (dateRange.from) {
        query = query.gte('date', dateRange.from.toISOString());
      }

      if (dateRange.to) {
        query = query.lte('date', dateRange.to.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Database error:', error);
        throw new Error("Failed to fetch events. Please try again.");
      }

      // Set the first event as featured if available
      if (data && data.length > 0) {
        setFeaturedEvent(data[0]);
        setEvents(data.slice(1));
      } else {
        setFeaturedEvent(null);
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: "Error",
        description: "Failed to fetch events. Please try again.",
        variant: "destructive",
      });
      setFeaturedEvent(null);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // If no user is logged in, redirect to auth page
  useEffect(() => {
    if (!user && !loading) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  if (!user) {
    return null; // Will redirect due to useEffect
  }

  return (
    <div className="container py-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Discover Events</h1>
        <Button onClick={() => navigate("/create-event")} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Event
        </Button>
      </div>

      {featuredEvent && (
        <div className="mb-12">
          <FeaturedEvent
            title={featuredEvent.title}
            description={featuredEvent.description || "Join us for this amazing event!"}
            date={new Date(featuredEvent.date).toLocaleDateString()}
            location={featuredEvent.location || "TBA"}
            imageUrl={featuredEvent.image_url || "/placeholder.svg"}
            price={featuredEvent.price ? `$${featuredEvent.price}` : "Free"}
          />
        </div>
      )}

      <div className="mb-8 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <SearchBar onSearch={handleSearch} />
          <EventFilters onDateRangeChange={setDateRange} />
        </div>
      </div>

      <div className="space-y-8">
        {loading ? (
          <div className="text-center text-gray-500">Loading events...</div>
        ) : events.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {events.map((event) => (
              <EventCard
                key={event.id}
                id={event.id}
                title={event.title}
                date={new Date(event.date).toLocaleDateString()}
                endDate={new Date(event.end_date).toLocaleDateString()}
                location={event.location}
                imageUrl={event.image_url}
                category={event.category}
                createdBy={event.profiles?.email || 'Unknown'}
                expectedAttendees={event.expected_attendees}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500">No events found</div>
        )}
      </div>
    </div>
  );
};

export default Discover;
